'use client';

import React, { useEffect, useRef, Suspense } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '../../../store';
import { togglePanel, setConnectors } from '../../../store/slices/chatSlice';
import chatService from '../../../services/api/chatService';
import { Button } from '@/components/ui/button';
import { MessageSquare } from 'lucide-react';

const ChatPanel = React.lazy(() =>
  import('./ChatPanel').then(mod => ({ default: mod.ChatPanel }))
);

export const ChatButton: React.FC = React.memo(() => {
  const dispatch = useDispatch<AppDispatch>();
  const { connectors, isPanelOpen } = useSelector((state: RootState) => state.chat);
  const isAuthenticated = useSelector((state: RootState) => (state.auth as any).isAuthenticated);
  const activeAccountId = useSelector((state: RootState) => (state.auth as any).activeAccountId);

  // In-flight guard so auth flips / re-renders don't spam the endpoint.
  const fetchingRef = useRef(false);

  // Track the account ID that connectors were last fetched for.
  // When it changes (account switch), force a re-fetch even if
  // connectors.length > 0 (stale data from the previous account).
  const lastFetchedAccountRef = useRef<string | null>(null);

  useEffect(() => {
    if (!isAuthenticated) return;
    if (fetchingRef.current) return;

    const accountChanged = activeAccountId !== lastFetchedAccountRef.current;

    // Skip fetch if connectors are already loaded for THIS account
    // and the panel isn't open (no need to refresh).
    if (!accountChanged && connectors.length > 0 && !isPanelOpen) return;

    fetchingRef.current = true;
    chatService.getUserConnectors()
      .then(result => {
        dispatch(setConnectors(result));
        lastFetchedAccountRef.current = activeAccountId;
      })
      .finally(() => {
        fetchingRef.current = false;
      });
  }, [isAuthenticated, isPanelOpen, connectors.length, activeAccountId, dispatch]);

  // Don't show if no connectors available
  if (!isAuthenticated || connectors.length === 0) return null;

  return (
    <>
      {/* Floating Action Button */}
      {!isPanelOpen && (
        <Button
          onClick={() => dispatch(togglePanel())}
          size="icon"
          className="fixed right-6 z-[1001] h-12 w-12 rounded-full shadow-lg bottom-[calc(60px+env(safe-area-inset-bottom)+1rem)] md:bottom-6"
          aria-label="Open chat"
        >
          <MessageSquare className="h-5 w-5" />
        </Button>
      )}

      {/* Chat Panel (lazy loaded) */}
      {isPanelOpen && (
        <Suspense fallback={null}>
          <ChatPanel />
        </Suspense>
      )}
    </>
  );
});

ChatButton.displayName = 'ChatButton';
